import { useShallow } from 'zustand/react/shallow';
import { ThreeDDesignLoader } from './3d';
import {
  calculate3dLocation,
  changeInchTo3dUnit,
  initBundleProducts,
  getPatternSetSurfaces,
} from './helpers';
import { TModel, useBundleStore } from './hooks/bundle-store';
import { useEffect, useState } from 'react';
import { Loader } from './components';
import WebGL from 'three/examples/jsm/capabilities/WebGL.js';
import useThemeStore, { SHOP_CODE } from './hooks/use-theme-store';
import { useMediaQuery } from './hooks/use-media-query';
import axios from 'axios';
import configService from '../config/config.service';
import { THEME_COLORS } from '@3d/frontend/calculator';

declare global {
  interface Window {
    calculatorState: string;
  }
}

function Preview3d() {
  const [show3d, setShow3d] = useState(true);
  const [loading, setLoading] = useState(true);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalPriceSymbol, setTotalPriceSymbol] = useState('$');
  const [isWebglAvailable, setIsWebglAvailable] = useState(true);
  const isMobile = useMediaQuery('(max-width: 750px)');
  const [pendingActions, setPendingActions] = useState<
    {
      type: string;
      sectionId: string;
      visible?: boolean;
      specCode?: string;
      specValueCode?: string;
    }[]
  >([]);

  const { setThemeCode, shopCode } = useThemeStore(
    useShallow((state) => ({
      setThemeCode: state.setThemeCode,
      shopCode: state.themeCode,
    })),
  );

  useEffect(() => {
    if (window) {
      const codeMap = { expo: 'ex', milo: 'mi', vivid: 'vt', squad: 'ap' };
      const code =
        Object.entries(codeMap).find(([key]) => new RegExp(key).test(window.location.href))?.[1] ||
        'ex';
      const validShopCodes: SHOP_CODE[] = ['ex', 'mi', 'vt', 'ap'];

      if (!validShopCodes.includes(code as SHOP_CODE)) {
        console.error(`Invalid shopCode: ${code}`);
        return;
      }
      const colors = THEME_COLORS[code as SHOP_CODE];

      if (colors) {
        Object.entries(colors).forEach(([key, value]) => {
          document.documentElement.style.setProperty(`--${key}-color`, value as string);
        });
      }

      console.log('3D_PREVIEW - SHOP', code);
      setThemeCode(code as SHOP_CODE);
    }
  }, []);

  useEffect(() => {
    setIsWebglAvailable(WebGL.isWebGL2Available());
  }, []);

  const {
    init,
    changeSpace,
    setActiveSectionVisibility,
    bundleExists,
    setEnableZoom,
    reset,
    updatePatternSetOrColorTheme,
  } = useBundleStore(
    useShallow((state) => ({
      init: state.init,
      changeSpace: state?.changeSpace,
      setActiveSectionVisibility: state.setActiveSectionVisibility,
      bundleExists: Boolean(state.bundle?.models),
      setEnableZoom: state?.setEnableZoom,
      reset: state?.reset,
      updatePatternSetOrColorTheme: state?.updatePatternSetOrColorTheme,
    })),
  );

  useEffect(() => {
    const handleError = (event: any) => {
      if (event.filename && event.message.includes('cross-origin')) {
        console.log(`Cross-origin error: ${event.message}`);
      } else {
        console.log(`Error: ${event.message}`);
      }
    };
    window.addEventListener('error', handleError);

    window.onerror = function (message, source, lineno, colno, error) {
      console.log(`Uncaught error: ${message} at ${source}:${lineno}:${colno}`);
    };
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);

  useEffect(() => {
    window.addEventListener('selectedPatternSet', (event: any) => {
      const patternSet = event?.detail;
      console.log('Change PatternSet', patternSet);
      updatePatternSetOrColorTheme(patternSet, 'patternSet');
    });
    window.addEventListener('selectedColorTheme', (event: any) => {
      const colorTheme = event?.detail;
      console.log('Change Selected Color Theme', colorTheme);
      updatePatternSetOrColorTheme(colorTheme, 'colorTheme');
    });
    window.addEventListener('syncCalcData', (event: any) => {
      const products = event?.detail?.data?.products;
      console.log('Sync Calculator Data', event);
      const sectionId = Object.keys(products)?.[0];
      if (sectionId) {
        console.log('Have change in Calculator');
      } else {
        return;
      }
      const value: any = Object.values(products)?.[0] as object;
      if ('selected' in value) {
        const result = setActiveSectionVisibility({
          sectionId,
          visible: value?.selected as boolean,
        });

        if (!result) {
          setPendingActions((prev) => [
            ...prev,
            { type: 'select', sectionId, visible: value?.selected as boolean },
          ]);
        }
      }
      if ('specs' in value) {
        const specCodes = Object.keys(value?.specs);
        const specs: any = Object.values(value?.specs);
        if (specCodes?.length > 0) {
          specCodes?.forEach((specCode: string, index: number) => {
            const spec = specs[index];
            const specValueCode = spec?.valueCode as string;

            const result = changeSpace({
              sectionId,
              specCode,
              specValueCode,
            });
            if (!result) {
              setPendingActions((prev) => [
                ...prev,
                { type: 'spec', sectionId, specCode, specValueCode },
              ]);
            }
          });
        }
      }
    });

    window.addEventListener('totalPrice', (event: any) => {
      setTotalPrice(event?.detail?.totalPrice);
      setTotalPriceSymbol(event?.detail?.symbol);
    });

    let holidayHumanModel: any;
    axios
      .get(configService.getBaseUrl(shopCode).restApi + '/holiday-human/today-holiday-human')
      .then((res) => {
        holidayHumanModel = res?.data?.model;
      })
      .catch((err) => console.error("Can't get holiday human model", err));

    window.addEventListener('initialApiData', (event: any) => {
      const bundleOrProduct = event?.detail?.apiData;
      const dummyModels = event?.detail?.dummyModels;
      const calcState = event?.detail?.calcState;
      const bundleProducts = bundleOrProduct?.bundleProducts;
      if (bundleOrProduct) {
        setShow3d(true);
        const isSingle = !bundleProducts;
        console.log((isSingle ? 'Get Product' : 'Get Bundle') + ' From Calculator: ');
        setTotalPrice(event?.detail?.totalPrice);
        setTotalPriceSymbol(event?.detail?.symbol);
        setEnableZoom(event?.detail?.enableZoom);
        const showBackLight = event?.detail?.showBackLight;
        const isApparel = isSingle ? false : bundleOrProduct?.isApparel;
        //init bundle items
        initBundleProducts({
          bundleProducts: !isSingle
            ? bundleProducts
            : [
                {
                  id: bundleOrProduct?.code,
                  product: bundleOrProduct,
                  textures: bundleOrProduct?.textures,
                },
              ],
          calcState,
          isApparel,
          bundle: bundleOrProduct,
          shopCode,
        }).then((result) => {
          const floorWidth = isSingle
            ? 0
            : result?.floorWidth
              ? result?.floorWidth
              : changeInchTo3dUnit(bundleOrProduct?.floorPlanDimensions?.width);
          const floorLength = isSingle
            ? 0
            : result?.floorLength
              ? result?.floorLength
              : changeInchTo3dUnit(bundleOrProduct?.floorPlanDimensions?.length);

          //init bundle human
          let isHumanExist = false;
          let humanModel;
          const bundleHuman = bundleOrProduct?.bundleHuman;
          const model = result?.models?.[0];
          const productHumanModel = model?.sections?.[0]?.product?.humanModel;
          const singleProductHaveHuman = isSingle && Boolean(productHumanModel);
          if (bundleHuman || singleProductHaveHuman) {
            isHumanExist = true;
            const humanM = holidayHumanModel?.id
              ? holidayHumanModel
              : singleProductHaveHuman
                ? productHumanModel
                : bundleHuman?.model;

            const humanL = bundleHuman?.location;
            humanModel = {
              id: 100,
              title: humanM?.shortTitle,
              src: humanM?._3DObject,
              code: 'HUMAN',
              location: singleProductHaveHuman
                ? {
                    z:
                      model?.dimensions?.length / 2 +
                      changeInchTo3dUnit(humanM?.dimensions?.length) / 2,
                    x: 0.3,
                    y: 0,
                    rotation: 0.5,
                  }
                : {
                    z: changeInchTo3dUnit(humanL?.left),
                    x: changeInchTo3dUnit(humanL?.top),
                    y: changeInchTo3dUnit(humanL?.height),
                    rotation: humanL?.rotation ?? 0,
                  },
              dimensions: {
                length: changeInchTo3dUnit(humanM?.dimensions?.length),
                width: changeInchTo3dUnit(humanM?.dimensions?.width),
                height: changeInchTo3dUnit(humanM?.dimensions?.height),
              },
              visible: true,
              sections: [],
            };
          }

          //init dummy models
          const finalDummyModels: TModel[] = dummyModels
            ? dummyModels?.map((dm: any, index: number) => ({
                id: bundleProducts?.length + 1 + index,
                code: 'DUMMY_MODEL',
                src: dm?.model?._3DObject,
                title: dm?.model?.shortTitle,
                location: {
                  z: changeInchTo3dUnit(dm?.location?.left),
                  x: changeInchTo3dUnit(dm?.location?.top),
                  y: changeInchTo3dUnit(dm?.location?.height),
                  rotation: dm?.location?.rotation ?? 0,
                },
                dimensions: {
                  length: changeInchTo3dUnit(dm?.model?.dimensions?.length),
                  width: changeInchTo3dUnit(dm?.model?.dimensions?.width),
                  height: changeInchTo3dUnit(dm?.model?.dimensions?.height),
                },
                sections: [],
              }))
            : [];
          //end of init dummy models

          const models = humanModel ? [...(result?.models || []), humanModel] : result?.models;

          const finalModels = !isSingle
            ? calculate3dLocation({
                ...result,
                floorWidth,
                floorLength,
                models: [...models, ...finalDummyModels],
              })
            : models;

          //manage height filed for single products
          if (isSingle && bundleOrProduct?.height) {
            finalModels[0].location = {
              y: changeInchTo3dUnit(bundleOrProduct?.height),
            };
          }
          const patternSetSurfaces = getPatternSetSurfaces(bundleOrProduct?.patternSet?.items);
          init(
            {
              isSingle,
              isApparel,
              isHumanExist,
              models: finalModels,
              floorLength,
              floorWidth,
              isFloorPlanModelExist: !!result?.floorLength,
              surfaceIds: result?.surfaceIds,
              colorTheme: bundleOrProduct?.patternSet?.colorTheme,
              patternSetSurfaces,
            },
            { itemsHaveBacklit: result?.itemsHaveBacklit, showBackLight },
          );
          console.log((isSingle ? 'Product' : 'Bundle') + ' Data Loaded');
          setLoading(false);
        });
      } else {
        reset();
        setShow3d(false);
      }
    });

    const _3dEventLoaded = new CustomEvent('on3DModelLoaded');
    const calcLoadEvent = 'checkoutUtilityReady';

    if (window.calculatorState === 'loaded') {
      window?.dispatchEvent(_3dEventLoaded);
      console.log('Preview 3D Loaded');
    } else {
      window.addEventListener(calcLoadEvent, () => {
        console.log('Preview 3D Loaded');
        window?.dispatchEvent(_3dEventLoaded);
      });
    }
  }, []);

  useEffect(() => {
    if (!loading) {
      if (pendingActions.length > 0 && bundleExists) {
        pendingActions.forEach((action) => {
          if (action.type === 'select') {
            setActiveSectionVisibility({
              sectionId: action.sectionId,
              visible: action.visible as boolean,
            });
          } else if (action.type === 'spec') {
            changeSpace({
              sectionId: action.sectionId,
              specCode: action.specCode as string,
              specValueCode: action.specValueCode as string,
            });
          }
        });
        setPendingActions([]);
      }
    }
  }, [loading, bundleExists]);

  return (
    <>
      {loading && <Loader />}
      <div
        style={{
          display: loading ? 'none' : 'block',
          width: '100%',
          height: '100%',
        }}>
        {!isWebglAvailable ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}>
            <div
              style={{
                maxWidth: '80%',
                backgroundColor: '#ffa39e',
                padding: '20px',
                borderRadius: '10px',
              }}>
              <h3 style={{ color: '#f5222d', fontSize: '16px' }}>Cannot load 3D preview.</h3>
              <p style={{ fontSize: '13px' }}>
                Please enable WebGL from your browser settings and reopen your browser
              </p>
            </div>
          </div>
        ) : (
          show3d && (
            <div
              style={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}>
              <ThreeDDesignLoader />
              {totalPrice > 0 && isMobile && (
                <p
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    fontFamily: 'Inter, sans-serif',
                    color: 'red',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    margin: '0px',
                  }}>
                  {totalPriceSymbol}
                  {totalPrice.toLocaleString('en-US', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}
                </p>
              )}
            </div>
          )
        )}
      </div>
    </>
  );
}

export default Preview3d;
